import { useMemo } from "react";
import {
  Box,
  Card,
  Heading,
  Link,
  List,
  ListItem,
  Show,
  Skeleton,
  SkeletonText,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { listingsModel } from "models/listings";
import { type APIMarketingCampaignEntry } from "models/types/api-listing";
import { useParams } from "react-router-dom";
import { EmptyText } from "components/empty-text";
import {
  ImageGallery,
  MobileImageGallery,
} from "components/image-gallery/image-gallery";
import { ScreenWrapper } from "components/screen-wrapper";
import { Table } from "components/table/table";
import { useFormatMoney } from "hooks/use-format-money";
import { useIsAgencyFeaturePreferenceEnabled } from "hooks/use-is-agency-feature-preference-enabled";
import { SkeletonList } from "modules/vendor-portal/components/skeleton-list";
import { MarketingMaterialHeading } from "modules/vendor-portal/screens/marketing/components/marketing-material-heading";
import { dateFormats } from "utils/date";

const getColumns = (
  formatMoney: ReturnType<typeof useFormatMoney>,
  excludePrice = false,
) => [
  {
    label: "Provider",
    id: "provider",
    formatValue: (value: { id: string; label: string }) => value.label,
  },
  {
    label: "Item",
    id: "product",
    formatValue: (value: { id: string; label: string }) => value.label,
  },
  {
    label: "Date",
    id: "booking_date",
    formatValue: (value: string) =>
      value ? dayjs(value).format(dateFormats.dayMonthYear) : "",
  },
  ...(excludePrice
    ? []
    : [
        {
          label: "Price",
          id: "price_inc_tax",
          formatValue: (value: number) => formatMoney(value / 100),
        },
      ]),
];

const getColumnsWithoutPrice = (
  formatMoney: ReturnType<typeof useFormatMoney>,
) => getColumns(formatMoney, true);

function sortMarketingItems(
  items: APIMarketingCampaignEntry[],
): APIMarketingCampaignEntry[] {
  return items.sort((a, b) =>
    a.booking_date && b.booking_date
      ? new Date(a.booking_date).getTime() - new Date(b.booking_date).getTime()
      : a.booking_date
        ? -1
        : 1,
  );
}

export function MarketingScreen() {
  const formatMoney = useFormatMoney();
  const { propertyId } = useParams();
  const isFeaturePreferenceEnabled = useIsAgencyFeaturePreferenceEnabled();

  const columns = useMemo(() => getColumns(formatMoney), [formatMoney]);
  const columnsWithoutPrice = useMemo(
    () => getColumnsWithoutPrice(formatMoney),
    [formatMoney],
  );

  const { data, isLoading, isFetching } = useQuery(
    listingsModel.queries.read(propertyId),
  );
  if (!data) return null;

  const defaultListingAdvert = data.marketing?.adverts.find(
    (i) => i.type.id === "internet",
  );

  const images = data.marketing?.images ?? [];
  const floorplans = data.marketing?.floorplans ?? [];

  return (
    <ScreenWrapper>
      <Heading size={{ base: "2xl", md: "lg" }} fontWeight={700} as={"h1"}>
        Marketing
      </Heading>

      <MarketingMaterialHeading />

      <Card p={6}>
        <VStack gap={3} alignItems={"flex-start"}>
          <Text fontWeight={700}>Imagery</Text>
          <Skeleton isLoaded={!isLoading}>
            <Show above={"md"}>
              {images.length > 0 ? (
                <ImageGallery images={images} emptyMessage={""} />
              ) : (
                <EmptyText>No imagery added</EmptyText>
              )}
            </Show>
            <Show below={"md"}>
              {images.length > 0 ? (
                <MobileImageGallery images={images} emptyMessage={""} />
              ) : (
                <EmptyText>No imagery added</EmptyText>
              )}
            </Show>
          </Skeleton>
        </VStack>
      </Card>

      <Card p={6}>
        <VStack gap={3} alignItems={"flex-start"}>
          <Text fontWeight={700}>Floor plans</Text>
          <Skeleton isLoaded={!isLoading} w={"100%"}>
            <Show above={"md"}>
              {floorplans.length > 0 ? (
                <ImageGallery images={floorplans} emptyMessage={""} />
              ) : (
                <EmptyText>No floor plan added</EmptyText>
              )}
            </Show>
            <Show below={"md"}>
              {images.length > 0 ? (
                <MobileImageGallery images={floorplans} emptyMessage={""} />
              ) : (
                <EmptyText>No floor plan added</EmptyText>
              )}
            </Show>
          </Skeleton>
        </VStack>
      </Card>

      <Card p={6}>
        <VStack gap={3} alignItems={"flex-start"}>
          <Text fontWeight={700}>Header</Text>
          <SkeletonText
            isLoaded={!isLoading}
            w={"100%"}
            noOfLines={2}
            spacing={"3"}
            skeletonHeight={"2"}
          >
            {defaultListingAdvert?.heading ? (
              <Text>{defaultListingAdvert.heading}</Text>
            ) : (
              <EmptyText>No header added</EmptyText>
            )}
          </SkeletonText>
        </VStack>
      </Card>

      <Card p={6}>
        <VStack gap={3} alignItems={"flex-start"}>
          <Text fontWeight={700}>Description</Text>
          <SkeletonText
            isLoaded={!isLoading}
            w={"100%"}
            noOfLines={8}
            spacing={"3"}
            skeletonHeight={"2"}
          >
            {defaultListingAdvert?.body ? (
              <Text whiteSpace={"pre-line"}>{defaultListingAdvert.body}</Text>
            ) : (
              <EmptyText>No description added</EmptyText>
            )}
          </SkeletonText>
        </VStack>
      </Card>

      <Card p={6}>
        <VStack gap={3} alignItems={"flex-start"}>
          <Text fontWeight={700}>Links</Text>
          <SkeletonList
            isLoaded={!isLoading}
            listSize={4}
            w={"100%"}
            skeleton={<Skeleton h={"16px"} />}
          >
            <List spacing={3}>
              {(data.marketing?.links?.length || 0) > 0 ? (
                data.marketing?.links?.map((link) => (
                  <ListItem key={link.url}>
                    <Text fontSize={"sm"} fontWeight={600}>
                      {link.type.label}
                    </Text>
                    <Link
                      href={link.url}
                      isExternal
                      color={"blue.600"}
                      fontSize={"lg"}
                      fontWeight={500}
                    >
                      {link.url}
                    </Link>
                  </ListItem>
                ))
              ) : (
                <EmptyText>No links added</EmptyText>
              )}
            </List>
          </SkeletonList>
        </VStack>
      </Card>

      {(data.marketing?.campaign?.entries.length || 0) > 0 && (
        <>
          <Heading fontSize={{ base: "xl", md: "xl" }} fontWeight={600}>
            Marketing schedule
          </Heading>
          <Box
            p={0}
            border={"1px solid"}
            borderColor={"gray.200"}
            borderRadius={"8px"}
            overflow={"hidden"}
          >
            <Table
              isLoading={isLoading}
              isFetching={isFetching}
              columns={
                isFeaturePreferenceEnabled(
                  "vendor_portal.functionality.marketing.show_marketing_costs",
                )
                  ? columns
                  : columnsWithoutPrice
              }
              rows={sortMarketingItems(data.marketing?.campaign?.entries ?? [])}
              emptyProps={{
                emptyMessage: "No marketing items found.",
                emptyDesc: "Please check back later.",
              }}
            />
          </Box>
        </>
      )}
    </ScreenWrapper>
  );
}
